import * as React from 'react';
import { createPortal } from 'react-dom';
import { MoreVertical } from 'lucide-react';
import {
  CrossCheckClause,
  CrossCheckMatter,
  CrossCheckInsight,
  BankOfChangesMulti,
  BankOfChangesOption,
  PastAgreementOption,
  getPrimaryBankOfChangesText,
  getBocOptionCount,
  formatClauseNumberForDisplay,
} from '../../types/crossCheck';
import { detectTextDirection } from '../../utils/textUtils';
import { TrackChangesParser } from '../../word/utils/TrackChangesParser';
import { RobustXmlTrackChangesGenerator } from '../../features/rephrasing/RobustXmlTrackChangesGenerator';
import { RedlineInserter } from '../../word/trackChanges/RedlineInserter';
import { DocumentReader } from '../../word/api/DocumentReader';
import apiService from '../../services/api';
import { PastNegotiationSourceModal } from './PastNegotiationSourceModal';
import './CrossCheckClauseCard.css';

interface CrossCheckClauseCardProps {
  clause: CrossCheckClause;
  index: number;
  projectId?: string;
  subProjectId?: string;
  onToggleLock?: (clause: CrossCheckClause) => void;
  isUpdatingLock?: boolean;
  onResponseSubmitted?: (clauseId: string) => void;
  onResponseUndone?: (clauseId: string) => void;
  submittedAtForClause?: number;
  /** Batched from CrossCheckResultsView — avoids N parallel playbook HTTP calls per page load. */
  pastAgreementPlaybookXmlById?: Record<string, string>;
  pastAgreementPlaybookSideById?: Record<string, 'our_side' | 'their_side'>;
  /** Resolved from playbook record → files table (batch API). */
  pastAgreementPlaybookFileNameById?: Record<string, string>;
  pastAgreementPlaybookFileIdById?: Record<string, string>;
  /** Plain paragraph_text resolved from the playbook by-ids batch API (replaces persisted opt.text). */
  pastAgreementPlaybookPlainTextById?: Record<string, string>;
  pastAgreementPlaybooksLoading?: boolean;
  pastAgreementDomain?: 'construction-loan' | 'real-estate';
  /**
   * Called when the user clicks the "Go to" badge in the Sales Agreement section.
   * Implemented by the parent so the PDF preview can be shown as a sticky split
   * panel above all cards (not as an in-card modal). When omitted the badge is
   * hidden — this matches the dashboard's "no source PDF available" state.
   */
  onGoToSalesAgreementSource?: (clause: CrossCheckClause, searchText?: string) => void;
  /** True when this card's clause is the one currently shown in the split panel. */
  isSalesAgreementSourceActive?: boolean;
  /** True while we're loading the SA PDF for this clause specifically. */
  isSalesAgreementSourceLoading?: boolean;
  onMoveClause?: (
    clause: CrossCheckClause,
    target: 'sales_agreement' | 'appendix_a' | 'saved_for_later',
  ) => void;
  /** Construction Loan: move between Commercial / Legal / Other (no re-analysis). */
  onMoveClauseMatter?: (clause: CrossCheckClause, target: CrossCheckMatter) => void;
  onAnalyzeAgain?: (clause: CrossCheckClause) => void;
  /** Construction Loan: open playbook agent chat in split view. */
  onAskAgent?: (clause: CrossCheckClause) => void;
  /** True while a move/reanalyze request is in flight for this clause. */
  isMutating?: boolean;
  /** MCC document-type menu vs Construction Loan matter menu. */
  kebabVariant?: 'mcc' | 'construction-loan';
  /** Ask Agent: streaming AI suggestion onto this card's client request. */
  externalAiStreaming?: boolean;
  /** Ask Agent: injected track-changes XML after chat-based suggestion. */
  externalEditedXml?: string | null;
  /** Ref on the card header row — used to scroll into view when Ask Agent opens. */
  cardHeaderRef?: React.Ref<HTMLDivElement>;
}

const getDir = (text: string | undefined): 'rtl' | 'ltr' => {
  if (!text) return 'ltr';
  return detectTextDirection(text);
};

function getInsightExistingClauseParagraphIndex(insight: CrossCheckInsight): number | null {
  if (insight.paragraph_index != null && !Number.isNaN(Number(insight.paragraph_index))) {
    return Number(insight.paragraph_index);
  }
  const cm = insight.chunk_metadata?.paragraph_index;
  if (cm != null && !Number.isNaN(Number(cm))) {
    return Number(cm);
  }
  return null;
}

function getInsightExistingClauseSearchText(insight: CrossCheckInsight): string {
  const t = insight.exist_clause?.trim();
  if (t) return t;
  const xml = insight.exist_clause_xml?.trim();
  if (xml) {
    try {
      const accepted = TrackChangesParser.extractAcceptedText(xml).trim();
      if (accepted) return accepted;
      return TrackChangesParser.extractText(xml).trim();
    } catch {
      return '';
    }
  }
  return '';
}

function canNavigateToInsightExistingClause(insight: CrossCheckInsight): boolean {
  return (
    getInsightExistingClauseParagraphIndex(insight) != null ||
    getInsightExistingClauseSearchText(insight).length > 0
  );
}

/** Prefer the sole insight; otherwise the first with text usable for SA PDF go-to. */
function pickInsightForSalesAgreementGoTo(
  insights: CrossCheckInsight[],
): CrossCheckInsight | null {
  if (!insights.length) return null;
  if (insights.length === 1) return insights[0];
  return insights.find((ins) => getInsightExistingClauseSearchText(ins).length > 0) ?? null;
}

/** Extract first suggestedResponse from partial streaming JSON (dashboard ClauseCard parity). */
function extractSuggestedResponse(jsonText: string): string {
  try {
    const startIndex = jsonText.indexOf('"suggestedResponse"');
    if (startIndex === -1) return '';
    const colonIndex = jsonText.indexOf(':', startIndex);
    if (colonIndex === -1) return '';
    const quoteStartIndex = jsonText.indexOf('"', colonIndex);
    if (quoteStartIndex === -1) return '';

    let result = '';
    let i = quoteStartIndex + 1;
    while (i < jsonText.length) {
      const char = jsonText[i];
      if (char === '\\') {
        if (i + 1 < jsonText.length) {
          const nextChar = jsonText[i + 1];
          if (nextChar === 'n') {
            result += '\n';
            i += 2;
          } else if (nextChar === '"') {
            result += '"';
            i += 2;
          } else if (nextChar === '\\') {
            result += '\\';
            i += 2;
          } else {
            result += char;
            i++;
          }
        } else {
          result += char;
          i++;
        }
      } else if (char === '"') {
        break;
      } else {
        result += char;
        i++;
      }
    }
    return result;
  } catch {
    return '';
  }
}

/** Extract all suggestedResponse values from partial streaming JSON (multi-insight modal). */
function extractAllSuggestedResponses(jsonText: string): string[] {
  const results: string[] = [];
  const key = `"suggestedResponse": "`;
  let searchIndex = 0;
  while (true) {
    const startIndex = jsonText.indexOf(key, searchIndex);
    if (startIndex === -1) break;

    let result = '';
    let i = startIndex + key.length;
    let inString = true;
    while (i < jsonText.length && inString) {
      const char = jsonText[i];
      if (char === '\\') {
        if (i + 1 < jsonText.length) {
          const nextChar = jsonText[i + 1];
          if (nextChar === 'n') {
            result += '\n';
            i += 2;
          } else if (nextChar === '"') {
            result += '"';
            i += 2;
          } else if (nextChar === '\\') {
            result += '\\';
            i += 2;
          } else {
            result += char;
            i++;
          }
        } else {
          result += char;
          i++;
        }
      } else if (char === '"') {
        inString = false;
        break;
      } else {
        result += char;
        i++;
      }
    }
    if (result.length > 0) results.push(result);
    searchIndex = startIndex + key.length;
  }
  return results;
}

function formatInsightsForApi(
  insights: CrossCheckInsight[],
  moduleType?: string,
): Array<Record<string, unknown>> {
  const isContracts = moduleType === 'module_b';
  const isXmlFormat = isContracts && insights.some((insight) => insight.exist_clause_xml);
  return insights.map((insight) => {
    if (isXmlFormat) {
      return {
        exist_clause_xml: insight.exist_clause_xml || insight.exist_clause,
        explanation: insight.explanation,
        file_id: insight.file_id,
        paragraph_index: insight.paragraph_index,
        original_xml: insight.original_xml,
        edited_xml: insight.edited_xml,
      };
    }
    return {
      exist_clause: insight.exist_clause || insight.exist_clause_xml,
      explanation: insight.explanation,
    };
  });
}

interface InsightAiSuggestion {
  clauseReference: string;
  explanation: string;
  suggestedResponse: string | null;
}

function buildAiSuggestionPreviewXml(
  clientRequestXml: string | null,
  clientRequestPlainText: string,
  suggestedText: string,
): string | null {
  const trimmed = suggestedText.trim();
  if (!trimmed) return null;
  if (
    trimmed.startsWith('<w:') ||
    trimmed.startsWith('<pkg:') ||
    trimmed.startsWith('<?xml') ||
    trimmed.includes('<w:p')
  ) {
    return trimmed;
  }
  const crXml =
    clientRequestXml || TrackChangesParser.createMinimalParagraphOoxml(clientRequestPlainText);
  try {
    return RobustXmlTrackChangesGenerator.generateTrackChangesXml(crXml, trimmed, 'AI Suggestion');
  } catch {
    return null;
  }
}

function convertClientRequestXmlToHtml(xml: string): string | null {
  try {
    return TrackChangesParser.convertOoxmlToHtml(xml);
  } catch {
    return null;
  }
}

interface ClientRequestXmlPreviewProps {
  xml: string | null | undefined;
  dir?: 'rtl' | 'ltr';
  className?: string;
}

/** Same track-changes HTML preview used in the Client Request box on the clause card. */
function ClientRequestXmlPreview({ xml, dir = 'ltr', className }: ClientRequestXmlPreviewProps) {
  const html = React.useMemo(() => (xml ? convertClientRequestXmlToHtml(xml) : null), [xml]);
  if (!html) return null;
  return (
    <div className={`cc-paragraph-xml-display${className ? ` ${className}` : ''}`} dir={dir}>
      <div
        className="cc-selected-text-xml"
        dangerouslySetInnerHTML={{ __html: html }}
        dir={dir}
      />
    </div>
  );
}

interface InsightSuggestionPreviewProps {
  suggestedText: string;
  clientRequestXml: string | null;
  clientRequestPlainText: string;
}

/** Preview an insight AI suggestion with the same XML renderer as the clause card. */
function InsightSuggestionPreview({
  suggestedText,
  clientRequestXml,
  clientRequestPlainText,
}: InsightSuggestionPreviewProps) {
  const previewXml = React.useMemo(
    () => buildAiSuggestionPreviewXml(clientRequestXml, clientRequestPlainText, suggestedText),
    [clientRequestXml, clientRequestPlainText, suggestedText],
  );
  const dir = React.useMemo((): 'rtl' | 'ltr' => {
    if (previewXml) {
      try {
        return getDir(TrackChangesParser.extractText(previewXml)) as 'rtl' | 'ltr';
      } catch {
        // fall through
      }
    }
    return getDir(suggestedText) as 'rtl' | 'ltr';
  }, [previewXml, suggestedText]);

  if (previewXml) {
    return <ClientRequestXmlPreview xml={previewXml} dir={dir} />;
  }
  return (
    <p className="cc-section-text" dir={dir}>
      {suggestedText}
    </p>
  );
}

/** Lightbulb icon used in insights modal (matches dashboard). */
const InsightsLightbulbIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 20 20" fill="currentColor" aria-hidden>
    <path d="M11 3a1 1 0 10-2 0v1a1 1 0 102 0V3zM15.657 5.757a1 1 0 00-1.414-1.414l-.707.707a1 1 0 001.414 1.414l.707-.707zM18 10a1 1 0 01-1 1h-1a1 1 0 110-2h1a1 1 0 011 1zM5.05 6.464A1 1 0 106.464 5.05l-.707-.707a1 1 0 00-1.414 1.414l.707.707zM5 10a1 1 0 01-1 1H3a1 1 0 110-2h1a1 1 0 011 1zM8 16v-1h4v1a2 2 0 11-4 0zM12 14c.015-.34.208-.646.477-.859a4 4 0 10-4.954 0c.27.213.462.519.476.859h4.002z" />
  </svg>
);

interface SalesAgreementGoToBadgeProps {
  onClick: () => void;
  disabled?: boolean;
  isActive?: boolean;
  isLoading?: boolean;
  className?: string;
  title?: string;
}

/** Shared "Go to" badge — Sales Agreement section and insights modal. */
const SalesAgreementGoToBadge: React.FC<SalesAgreementGoToBadgeProps> = ({
  onClick,
  disabled = false,
  isActive = false,
  isLoading = false,
  className = '',
  title,
}) => (
  <button
    type="button"
    className={`cc-sa-go-to-badge${isActive ? ' cc-sa-go-to-active' : ''}${className ? ` ${className}` : ''}`}
    onClick={(e) => {
      e.stopPropagation();
      onClick();
    }}
    disabled={disabled || isLoading}
    title={
      title ??
      (isActive
        ? 'Close Sales Agreement PDF view'
        : 'Open Sales Agreement PDF and jump to this clause')
    }
    aria-pressed={isActive}
  >
    {isLoading ? (
      <>
        <span>Loading…</span>
        <span className="cc-sa-go-to-spinner" aria-hidden />
      </>
    ) : isActive ? (
      <span>Close</span>
    ) : (
      <>
        <svg className="cc-sa-go-to-badge-icon" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
          <path
            fillRule="evenodd"
            d="M10.293 3.293a1 1 0 011.414 0l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414-1.414L13.586 10H4a1 1 0 110-2h9.586l-3.293-3.293a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
        <span>Go to</span>
      </>
    )}
  </button>
);

function getStatusBadge(clause: CrossCheckClause): { label: string; className: string } {
  const hasClient = Boolean(clause.client_request?.trim());
  const hasSales = Boolean(clause.sales_agreement?.trim());
  const hasBoc = Boolean(getPrimaryBankOfChangesText(clause.bank_of_changes));

  if (clause.documentType === 'saved_for_later') {
    return { label: 'SAVED FOR LATER', className: 'cc-saved' };
  }
  if (hasClient && hasSales && hasBoc) {
    return { label: 'CLIENT REQUEST IDENTIFIED', className: 'cc-identified' };
  }
  if (hasClient && (hasSales || hasBoc)) {
    return { label: 'PARTIALLY MATCHED', className: 'cc-matched' };
  }
  if (hasClient) {
    return { label: 'CLIENT REQUEST IDENTIFIED', className: 'cc-identified' };
  }
  return { label: 'MISSING DATA', className: 'cc-missing' };
}

function getClientRequestXml(clause: CrossCheckClause): string | null {
  const cr = clause.client_request?.trim() || '';
  if (cr && (
    cr.startsWith('<w:') ||
    cr.startsWith('<pkg:') ||
    cr.startsWith('<?xml') ||
    cr.includes('<w:p') ||
    cr.includes('<p ')
  )) {
    return cr;
  }
  if (clause.track_changes?.paragraph_xml) {
    return clause.track_changes.paragraph_xml;
  }
  return null;
}

function getClientRequestPlainText(clause: CrossCheckClause, xml: string | null): string {
  if (xml) {
    // Use accepted text (exclude w:del, include w:ins) so plain string matches what the
    // client intends after their track changes — not raw textContent which still embeds
    // deleted words and confuses the AI suggestion endpoint when comparing to BoC.
    try {
      return TrackChangesParser.extractAcceptedText(xml).replace(/\s+/g, ' ').trim();
    } catch { /* fallthrough */ }
  }
  if (clause.module_type === 'module_b' && clause.client_request) {
    try {
      const parsed = JSON.parse(clause.client_request);
      return parsed.edited_text || parsed.original_text || clause.client_request;
    } catch {
      return clause.client_request;
    }
  }
  return clause.client_request || '';
}

function getBocOptions(clause: CrossCheckClause): BankOfChangesOption[] | null {
  const boc = clause.bank_of_changes;
  if (!boc) return null;
  if (typeof boc === 'object' && 'options' in boc) {
    return (boc as BankOfChangesMulti).options;
  }
  if (Array.isArray(boc) && boc.length > 0) {
    return boc as BankOfChangesOption[];
  }
  return null;
}

export const CrossCheckClauseCard: React.FC<CrossCheckClauseCardProps> = ({
  clause,
  index,
  projectId: projectIdProp,
  subProjectId,
  onToggleLock,
  isUpdatingLock = false,
  onResponseSubmitted,
  onResponseUndone,
  submittedAtForClause,
  pastAgreementPlaybookXmlById = {},
  pastAgreementPlaybookSideById = {},
  pastAgreementPlaybookFileNameById = {},
  pastAgreementPlaybookFileIdById = {},
  pastAgreementPlaybookPlainTextById = {},
  pastAgreementPlaybooksLoading = false,
  pastAgreementDomain = 'construction-loan',
  onGoToSalesAgreementSource,
  isSalesAgreementSourceActive = false,
  isSalesAgreementSourceLoading = false,
  onMoveClause,
  onMoveClauseMatter,
  onAnalyzeAgain,
  onAskAgent,
  isMutating = false,
  kebabVariant = 'mcc',
  externalAiStreaming = false,
  externalEditedXml = null,
  cardHeaderRef,
}) => {
  const [salesExpanded, setSalesExpanded] = React.useState(false);
  const [bocExpanded, setBocExpanded] = React.useState(false);
  const [showInsightModal, setShowInsightModal] = React.useState(false);
  const [showViewInAgreementConfirm, setShowViewInAgreementConfirm] = React.useState(false);
  const [showExplanationModal, setShowExplanationModal] = React.useState(false);
  const [insightAiSuggestions, setInsightAiSuggestions] = React.useState<InsightAiSuggestion[] | null>(null);
  const [isLoadingInsightAi, setIsLoadingInsightAi] = React.useState(false);
  const insightAiGenerationTriggeredRef = React.useRef(false);
  const [editingCr, setEditingCr] = React.useState(false);
  const [crEditValue, setCrEditValue] = React.useState('');
  const [editingBoc, setEditingBoc] = React.useState(false);
  const [bocEditValue, setBocEditValue] = React.useState('');
  const [addingBoc, setAddingBoc] = React.useState(false);
  const [bocNewValue, setBocNewValue] = React.useState('');
  const [selectedBocIndex, setSelectedBocIndex] = React.useState(0);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [submitResult, setSubmitResult] = React.useState<'success' | 'error' | null>(null);
  /** After AI suggestion: edited XML with track changes (like Negotiator currentEditedXml) */
  const [currentEditedXml, setCurrentEditedXml] = React.useState<string | null>(null);
  /** Client request OOXML after Accept (Word + card preview aligned with accepted text). */
  const [localAcceptedCrXml, setLocalAcceptedCrXml] = React.useState<string | null>(null);
  const [isStreaming, setIsStreaming] = React.useState(false);
  const [aiSourceMenuOpen, setAiSourceMenuOpen] = React.useState(false);
  const aiMenuRef = React.useRef<HTMLDivElement>(null);
  /** Bumped when the user clears or switches clause — ignores late AI stream results. */
  const aiSuggestionGenerationRef = React.useRef(0);

  // Past negotiations (Construction Loan) – simple read-only display of up to 5 matches
  const pastAgreementOptions = React.useMemo(() => {
    const pa: any = (clause as any).past_agreement;
    if (!pa || !Array.isArray(pa?.options)) return null;
    return pa.options as PastAgreementOption[];
  }, [clause]);
  const [pastAgreementExpanded, setPastAgreementExpanded] = React.useState(false);
  const [selectedPastAgreementIndex, setSelectedPastAgreementIndex] = React.useState(0);
  const pastAgreementXmlById = pastAgreementPlaybookXmlById;
  const pastAgreementSideById = pastAgreementPlaybookSideById;
  const pastAgreementFileNameById = pastAgreementPlaybookFileNameById;
  const pastAgreementFileIdById = pastAgreementPlaybookFileIdById;
  const pastAgreementPlainTextById = pastAgreementPlaybookPlainTextById;
  const isPastAgreementXmlLoading = pastAgreementPlaybooksLoading;

  /** Resolve paragraph plain text for a playbook id — batch map first, then legacy opt.text. */
  const getPastAgreementPlainText = React.useCallback(
    (optionId: string, legacyText?: string) =>
      pastAgreementPlainTextById[optionId] || legacyText || '',
    [pastAgreementPlainTextById],
  );

  const getPastAgreementSourceDisplay = React.useCallback(
    (playbookId: string) => {
      if (
        isPastAgreementXmlLoading &&
        !pastAgreementFileNameById[playbookId] &&
        !pastAgreementFileIdById[playbookId]
      ) {
        return 'Loading…';
      }
      const name = pastAgreementFileNameById[playbookId];
      if (name?.trim()) return name.trim();
      const fid = pastAgreementFileIdById[playbookId];
      if (fid?.trim()) return fid.trim();
      return 'Source unavailable';
    },
    [isPastAgreementXmlLoading, pastAgreementFileNameById, pastAgreementFileIdById],
  );
  const [pastAgreementExplanationModal, setPastAgreementExplanationModal] = React.useState<{
    text: string;
    rank: number;
  } | null>(null);
  const [pastAgreementSourceModal, setPastAgreementSourceModal] = React.useState<{
    rank: number;
    playbookId: string;
    searchText?: string;
  } | null>(null);
  const badge = getStatusBadge(clause);
  const clauseNumberDisplay = formatClauseNumberForDisplay(clause.clause_number);
  const bocText = getPrimaryBankOfChangesText(clause.bank_of_changes);
  const bocCount = getBocOptionCount(clause.bank_of_changes);
  const bocOptions = getBocOptions(clause);

  // Clamp selected BoC index when options change (e.g. different clause or list length)
  const safeSelectedBocIndex = React.useMemo(() => {
    if (!bocOptions || bocOptions.length === 0) return 0;
    return Math.min(Math.max(0, selectedBocIndex), bocOptions.length - 1);
  }, [bocOptions, selectedBocIndex]);

  // Displayed BoC text: selected option when list exists, otherwise primary
  const displayBocText = React.useMemo(() => {
    if (bocOptions && bocOptions.length > 0 && bocOptions[safeSelectedBocIndex]) {
      return bocOptions[safeSelectedBocIndex].text;
    }
    return bocText;
  }, [bocOptions, safeSelectedBocIndex, bocText]);

  const safeSelectedPastAgreementIndex = React.useMemo(() => {
    if (!pastAgreementOptions || pastAgreementOptions.length === 0) return 0;
    return Math.min(Math.max(0, selectedPastAgreementIndex), pastAgreementOptions.length - 1);
  }, [pastAgreementOptions, selectedPastAgreementIndex]);

  // Reset selected indices and AI suggestion when switching clause
  React.useEffect(() => {
    setSelectedBocIndex(0);
    setSelectedPastAgreementIndex(0);
    aiSuggestionGenerationRef.current += 1;
    setCurrentEditedXml(null);
    setLocalAcceptedCrXml(null);
    setIsStreaming(false);
    setAiSourceMenuOpen(false);
    setInsightAiSuggestions(null);
    setIsLoadingInsightAi(false);
    insightAiGenerationTriggeredRef.current = false;
  }, [clause.id]);

  React.useEffect(() => {
    if (externalEditedXml) {
      setCurrentEditedXml(externalEditedXml);
    }
  }, [externalEditedXml]);

  const isStreamingEffective = isStreaming || externalAiStreaming;

  const hasAiSuggestion = Boolean(currentEditedXml);
  const isResponseSubmitted = Boolean(clause.submission_status?.response_submitted);

  const handleClearAiSuggestion = React.useCallback(() => {
    aiSuggestionGenerationRef.current += 1;
    setCurrentEditedXml(null);
    setIsStreaming(false);
    setAiSourceMenuOpen(false);
  }, []);

  const selectedPastAgreementOption =
    pastAgreementOptions && pastAgreementOptions.length > 0
      ? pastAgreementOptions[safeSelectedPastAgreementIndex]
      : null;

  const displayPastAgreementText = React.useMemo(() => {
    if (!selectedPastAgreementOption) return '';
    return selectedPastAgreementOption.id
      ? getPastAgreementPlainText(selectedPastAgreementOption.id, selectedPastAgreementOption.text)
      : (selectedPastAgreementOption.text ?? '');
  }, [selectedPastAgreementOption, getPastAgreementPlainText]);

  const selectedPastAgreementXml = React.useMemo(() => {
    if (!selectedPastAgreementOption?.id) return null;
    return pastAgreementXmlById[selectedPastAgreementOption.id] || null;
  }, [selectedPastAgreementOption, pastAgreementXmlById]);

  const selectedPastAgreementHtml = React.useMemo(() => {
    if (!selectedPastAgreementXml) return null;
    try { return TrackChangesParser.convertOoxmlToHtml(selectedPastAgreementXml); } catch { return null; }
  }, [selectedPastAgreementXml]);
  const selectedPastAgreementSide = React.useMemo<'our_side' | 'their_side' | null>(() => {
    if (!selectedPastAgreementOption?.id) return null;
    return pastAgreementSideById[selectedPastAgreementOption.id] || null;
  }, [selectedPastAgreementOption, pastAgreementSideById]);

  const selectedPastAgreementDir = React.useMemo((): 'rtl' | 'ltr' => {
    if (selectedPastAgreementXml) {
      try {
        return getDir(TrackChangesParser.extractText(selectedPastAgreementXml)) as 'rtl' | 'ltr';
      } catch {
        // Fall back to text below
      }
    }
    const plainText = selectedPastAgreementOption?.id
      ? getPastAgreementPlainText(selectedPastAgreementOption.id, selectedPastAgreementOption.text)
      : (selectedPastAgreementOption?.text ?? '');
    return getDir(plainText) as 'rtl' | 'ltr';
  }, [selectedPastAgreementXml, selectedPastAgreementOption, getPastAgreementPlainText]);

  const hasInsights = clause.insights && clause.insights.length > 0;
  const primaryInsight = hasInsights ? clause.insights![0] : null;
  const isLocked = clause.lock_status?.locked;

  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [isMoveSubmenuOpen, setIsMoveSubmenuOpen] = React.useState(false);
  const [moveSubmenuPosition, setMoveSubmenuPosition] = React.useState<{ top: number; left: number } | null>(null);
  const menuRef = React.useRef<HTMLDivElement | null>(null);
  const moveMenuItemRef = React.useRef<HTMLButtonElement | null>(null);
  const moveSubmenuRef = React.useRef<HTMLDivElement | null>(null);
  const MOVE_SUBMENU_WIDTH = 176;

  const isClauseSubmitted =
    !!clause.submission_status?.schedule_submitted ||
    !!clause.submission_status?.response_submitted;
  const moveBlockedMessage =
    'This clause has already been submitted. Undo the submission before moving it.';
  const isClKebab = kebabVariant === 'construction-loan';
  const showKebabMenu =
    onMoveClause ||
    onMoveClauseMatter ||
    onAskAgent ||
    (onAnalyzeAgain &&
      (isClKebab ||
        clause.documentType === 'sales_agreement' ||
        clause.documentType === 'appendix_a'));

  const updateMoveSubmenuPosition = React.useCallback(() => {
    const anchor = moveMenuItemRef.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    const gap = 4;
    let left = rect.left - MOVE_SUBMENU_WIDTH - gap;
    if (left < 8) {
      left = rect.right + gap;
    }
    setMoveSubmenuPosition({ top: rect.top, left });
  }, []);

  React.useEffect(() => {
    if (!isMoveSubmenuOpen) {
      setMoveSubmenuPosition(null);
      return;
    }
    updateMoveSubmenuPosition();
    window.addEventListener('resize', updateMoveSubmenuPosition);
    window.addEventListener('scroll', updateMoveSubmenuPosition, true);
    return () => {
      window.removeEventListener('resize', updateMoveSubmenuPosition);
      window.removeEventListener('scroll', updateMoveSubmenuPosition, true);
    };
  }, [isMoveSubmenuOpen, updateMoveSubmenuPosition]);

  React.useEffect(() => {
    if (!isMenuOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const insideMenu = menuRef.current?.contains(target);
      const insideMoveSubmenu = moveSubmenuRef.current?.contains(target);
      if (!insideMenu && !insideMoveSubmenu) {
        setIsMenuOpen(false);
        setIsMoveSubmenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMenuOpen]);

  React.useEffect(() => {
    setIsMenuOpen(false);
    setIsMoveSubmenuOpen(false);
  }, [clause.id]);

  const clientRequestXml = React.useMemo(() => getClientRequestXml(clause), [clause]);
  const clientRequestPlainText = React.useMemo(() => getClientRequestPlainText(clause, clientRequestXml), [clause, clientRequestXml]);

  const displayXml = currentEditedXml ?? localAcceptedCrXml ?? clientRequestXml;
  const hasTrackChangesInClientRequest = React.useMemo(
    () => Boolean(displayXml && TrackChangesParser.hasTrackChangesMarkup(displayXml)),
    [displayXml],
  );
  const canAcceptTrackChanges =
    !isResponseSubmitted &&
    hasTrackChangesInClientRequest &&
    clause.paragraph_index != null &&
    !Number.isNaN(Number(clause.paragraph_index));

  /** Direction for Client Request: use plain text for detection, not raw XML (XML would be detected as LTR). */
  const clientRequestDir = React.useMemo((): 'rtl' | 'ltr' => {
    if (editingCr && crEditValue) return getDir(crEditValue) as 'rtl' | 'ltr';
    if (isStreamingEffective && clientRequestXml) {
      try {
        const plain = TrackChangesParser.extractText(clientRequestXml);
        return getDir(plain) as 'rtl' | 'ltr';
      } catch {
        return getDir(clientRequestPlainText) as 'rtl' | 'ltr';
      }
    }
    if (displayXml) {
      try {
        const plain = TrackChangesParser.extractText(displayXml);
        return getDir(plain) as 'rtl' | 'ltr';
      } catch {
        return getDir(clientRequestPlainText) as 'rtl' | 'ltr';
      }
    }
    return getDir(clientRequestPlainText) as 'rtl' | 'ltr';
  }, [editingCr, crEditValue, isStreamingEffective, clientRequestXml, displayXml, clientRequestPlainText]);

  /** Accept track changes in Word and mark clause as response-submitted (same outcome as Submit to Response). */
  const handleAcceptClientRequestTrackChanges = React.useCallback(async () => {
    if (isSubmitting || isResponseSubmitted) return;
    const paragraphIndex = clause.paragraph_index;
    if (paragraphIndex == null || Number.isNaN(Number(paragraphIndex))) return;
    const sourceXml = displayXml;
    if (!sourceXml || !TrackChangesParser.hasTrackChangesMarkup(sourceXml)) return;

    setIsSubmitting(true);
    setSubmitResult(null);
    try {
      await DocumentReader.navigateToParagraph(Number(paragraphIndex));
      const { acceptedCount, paragraphOoxml } =
        await RedlineInserter.acceptTrackedChangesInParagraph(Number(paragraphIndex));

      const acceptedXml =
        paragraphOoxml && !TrackChangesParser.hasTrackChangesMarkup(paragraphOoxml)
          ? paragraphOoxml
          : TrackChangesParser.acceptAllTrackChangesInOoxml(
              paragraphOoxml || sourceXml,
            );

      setLocalAcceptedCrXml(acceptedXml);
      if (currentEditedXml) {
        setCurrentEditedXml(acceptedXml);
      }

      if (acceptedCount === 0 && !paragraphOoxml) {
        console.warn(
          '[CrossCheckClauseCard] Accept: no Word tracked changes found; updated card preview only',
        );
      }

      if (subProjectId) {
        try {
          await apiService.updateClauseResponseSubmissionStatus(subProjectId, clause.id, true);
        } catch (backendErr) {
          console.warn(
            '[CrossCheckClauseCard] Accept: backend submit status update failed (Word changes still applied):',
            backendErr,
          );
        }
      }
      onResponseSubmitted?.(clause.id);
      setSubmitResult('success');
      setTimeout(() => setSubmitResult(null), 2500);
    } catch (err) {
      console.error('[CrossCheckClauseCard] Accept track changes failed:', err);
      setSubmitResult('error');
      setTimeout(() => setSubmitResult(null), 2500);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    isSubmitting,
    isResponseSubmitted,
    clause.id,
    clause.paragraph_index,
    currentEditedXml,
    displayXml,
    subProjectId,
    onResponseSubmitted,
  ]);

  /** Direction for Sales Agreement text. */
  const salesAgreementDir = React.useMemo(
    () => getDir(clause.sales_agreement) as 'rtl' | 'ltr',
    [clause.sales_agreement]
  );

  const resolvedProjectId = projectIdProp ?? undefined;

  // ─── AI source availability ──────────────────────────────────────────────────
  const hasBocForAi = Boolean(displayBocText?.trim());
  const hasPaForAi = Boolean(
    selectedPastAgreementOption?.id
      ? getPastAgreementPlainText(selectedPastAgreementOption.id, selectedPastAgreementOption.text).trim()
      : selectedPastAgreementOption?.text?.trim()
  );

  // ─── Click-outside handler for AI source menu ────────────────────────────────
  React.useEffect(() => {
    if (!aiSourceMenuOpen) return;
    const handler = (e: MouseEvent) => {
      if (aiMenuRef.current && !aiMenuRef.current.contains(e.target as Node)) {
        setAiSourceMenuOpen(false);
      }
    };
    const keyHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAiSourceMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', keyHandler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('keydown', keyHandler);
    };
  }, [aiSourceMenuOpen]);

  // ─── AI (master cross check): bank-of-change / past-agreement suggestion stream ─────
  const handleAiSuggestionFrom = React.useCallback(
    async (
      source: 'bank_of_changes' | 'past_agreement',
      opts?: {
        bankOfChangesOption?: BankOfChangesOption;
        pastAgreementOption?: PastAgreementOption;
      },
    ) => {
      if (!resolvedProjectId || isStreamingEffective) return;

      const paOpt =
        source === 'past_agreement'
          ? opts?.pastAgreementOption ?? selectedPastAgreementOption
          : null;
      const bocOpt =
        source === 'bank_of_changes'
          ? opts?.bankOfChangesOption ?? bocOptions?.[safeSelectedBocIndex]
          : null;

      const referenceText =
        source === 'past_agreement'
          ? (paOpt?.id
              ? getPastAgreementPlainText(paOpt.id, paOpt.text)
              : (paOpt?.text ?? '')
            ).trim()
          : (bocOpt?.text ?? displayBocText).trim();

      const referenceExplanation =
        source === 'past_agreement'
          ? (paOpt?.explanation ?? '').trim() || undefined
          : (bocOpt?.internal_note ?? '').trim() || undefined;

      if (!referenceText) return;

    const crXml = clientRequestXml || TrackChangesParser.createMinimalParagraphOoxml(clientRequestPlainText);
    const crText =
      clientRequestPlainText ||
      TrackChangesParser.extractAcceptedText(crXml) ||
      TrackChangesParser.extractText(crXml);

    const generation = aiSuggestionGenerationRef.current + 1;
    aiSuggestionGenerationRef.current = generation;
    setIsStreaming(true);
    let accumulatedText = '';
    let finalSuggestedText = '';

    try {
      for await (const chunk of apiService.generateBankOfChangeSuggestionStream(
        resolvedProjectId,
        crText,
        crXml,
        referenceText,
        source,
        referenceExplanation
      )) {
        if (chunk.type === 'text' && chunk.content) {
          accumulatedText += chunk.content;
        } else if (chunk.type === 'complete') {
          finalSuggestedText = chunk.suggested_text ?? accumulatedText;
        } else if (chunk.type === 'error') {
          throw new Error(chunk.error);
        }
        // 'xml_ready' from server is intentionally ignored: we always generate XML
        // client-side via RobustXmlTrackChangesGenerator so that existing track
        // changes in the client request are preserved (same approach as ClauseCard).
      }

      const normCr = crText.replace(/\s+/g, ' ').trim();
      const normFinal = finalSuggestedText.replace(/\s+/g, ' ').trim();
      const engineNoOp = Boolean(normFinal) && normFinal === normCr;

      // Always generate XML client-side so that pre-existing w:ins/w:del elements
      // in the original client-request XML are threaded through correctly.
      if (generation !== aiSuggestionGenerationRef.current) return;
      if (engineNoOp) return;
      if (finalSuggestedText && crXml) {
        try {
          const { RobustXmlTrackChangesGenerator } = await import('../../features/rephrasing/RobustXmlTrackChangesGenerator');
          const suggestedXml = RobustXmlTrackChangesGenerator.generateTrackChangesXml(
            crXml,
            finalSuggestedText,
            'AI Suggestion'
          );
          if (generation !== aiSuggestionGenerationRef.current) return;
          setCurrentEditedXml(suggestedXml);
        } catch (e) {
          console.error('[CrossCheckClauseCard] Generate track-changes XML failed:', e);
          if (generation !== aiSuggestionGenerationRef.current) return;
          setCurrentEditedXml(crXml);
        }
      }
    } catch (err) {
      console.error('[CrossCheckClauseCard] AI suggestion failed:', err);
    } finally {
      if (generation === aiSuggestionGenerationRef.current) {
        setIsStreaming(false);
      }
    }
    },
    [
      resolvedProjectId,
      isStreaming,
      displayBocText,
      selectedPastAgreementOption,
      getPastAgreementPlainText,
      bocOptions,
      safeSelectedBocIndex,
      clientRequestXml,
      clientRequestPlainText,
    ],
  );

  const applyInsightSuggestionToClientRequest = React.useCallback(
    (suggestedText: string, generation?: number) => {
      const crNorm = clientRequestPlainText.replace(/\s+/g, ' ').trim();
      const suggestedNorm = suggestedText.replace(/\s+/g, ' ').trim();
      const previewXml = buildAiSuggestionPreviewXml(
        clientRequestXml,
        clientRequestPlainText,
        suggestedText,
      );
      if (!previewXml) {
        console.log('[CrossCheckClauseCard] insight AI preview: no preview XML', {
          clauseId: clause.id,
          suggestedLen: suggestedNorm.length,
        });
        return;
      }
      if (generation !== undefined && generation !== aiSuggestionGenerationRef.current) return;
      const ooxmlAuthorAudit = TrackChangesParser.auditOoxmlTrackChangeAuthors(previewXml);
      const trackChangesHtml = TrackChangesParser.convertOoxmlToHtml(previewXml);
      const htmlStylingAudit = TrackChangesParser.auditHtmlTrackChangeStyling(trackChangesHtml);
      const opsSummary = RedlineInserter.summarizeTrackChangesHtmlOps(trackChangesHtml);
      const appendedOnly =
        crNorm.length > 0 &&
        suggestedNorm.startsWith(crNorm) &&
        suggestedNorm.length > crNorm.length;
      console.log('[CrossCheckClauseCard] insight AI preview applied (AI - Already Exist)', {
        clauseId: clause.id,
        crLen: crNorm.length,
        suggestedLen: suggestedNorm.length,
        appendedLen: appendedOnly ? suggestedNorm.length - crNorm.length : null,
        appendedOnly,
        trailingInsertOnly: opsSummary.trailingInsertOnly,
        anyInsertMissingSearchAnchor: opsSummary.anyInsertMissingSearchAnchor,
        opsSummary,
      });
      console.log('[TrackChangeAuthor] insight AI preview styling', {
        clauseId: clause.id,
        ooxmlAuthorAudit,
        htmlStylingAudit,
        likelyMisstyledAiInsert:
          ooxmlAuthorAudit.aiSuggestionIns > 0 && htmlStylingAudit.insNewBlue === 0,
        note: 'Blue sidebar = ins.track-change-new; red = counterparty/other author without track-change-new',
      });
      setCurrentEditedXml(previewXml);
    },
    [clientRequestXml, clientRequestPlainText, clause.id],
  );

  const handleGenerateInsightAiSuggestions = React.useCallback(async () => {
    if (!subProjectId || !hasInsights || !clientRequestPlainText?.trim()) {
      insightAiGenerationTriggeredRef.current = false;
      return;
    }
    if (isLoadingInsightAi) return;

    const insights = clause.insights!;
    const insightsForApi = formatInsightsForApi(insights, clause.module_type);

    try {
      setIsLoadingInsightAi(true);
      setInsightAiSuggestions(
        insights.map((insight, idx) => ({
          clauseReference: `Insight ${idx + 1}`,
          explanation: insight.explanation || '',
          suggestedResponse: '',
        })),
      );

      let accumulatedText = '';
      let finalSuggestions: InsightAiSuggestion[] | null = null;

      for await (const chunk of apiService.generateInsightResponsesStream(
        subProjectId,
        clientRequestPlainText,
        insightsForApi,
      )) {
        if (chunk.type === 'text') {
          accumulatedText += chunk.content || '';
          const extractedResponses = extractAllSuggestedResponses(accumulatedText);
          setInsightAiSuggestions((prev) => {
            if (!prev) return prev;
            return prev.map((suggestion, idx) => ({
              ...suggestion,
              suggestedResponse: extractedResponses[idx] ?? suggestion.suggestedResponse,
            }));
          });
        } else if (chunk.type === 'complete') {
          finalSuggestions =
            chunk.suggestions?.map((s, idx) => ({
              clauseReference: `Insight ${idx + 1}`,
              explanation: insights[idx]?.explanation || '',
              suggestedResponse: s.suggestedResponse ?? null,
            })) ?? null;
          if (finalSuggestions) {
            setInsightAiSuggestions(finalSuggestions);
          }
        } else if (chunk.type === 'error') {
          throw new Error(chunk.error);
        }
      }

      if (!finalSuggestions) {
        const extracted = extractAllSuggestedResponses(accumulatedText);
        if (extracted.length > 0) {
          setInsightAiSuggestions(
            insights.map((insight, idx) => ({
              clauseReference: `Insight ${idx + 1}`,
              explanation: insight.explanation || '',
              suggestedResponse: extracted[idx] ?? null,
            })),
          );
        }
      }
    } catch (err) {
      console.error('[CrossCheckClauseCard] Insight AI suggestions failed:', err);
      insightAiGenerationTriggeredRef.current = false;
    } finally {
      setIsLoadingInsightAi(false);
    }
  }, [
    subProjectId,
    hasInsights,
    clientRequestPlainText,
    clause.insights,
    clause.module_type,
    isLoadingInsightAi,
  ]);

  const handleApplyInsightSuggestion = React.useCallback(
    (suggestedText: string | null | undefined) => {
      if (!suggestedText?.trim()) return;
      aiSuggestionGenerationRef.current += 1;
      applyInsightSuggestionToClientRequest(suggestedText);
      setShowInsightModal(false);
      setInsightAiSuggestions(null);
      setIsLoadingInsightAi(false);
      insightAiGenerationTriggeredRef.current = false;
    },
    [applyInsightSuggestionToClientRequest],
  );

  const closeInsightModal = React.useCallback(() => {
    setShowInsightModal(false);
    setInsightAiSuggestions(null);
    setIsLoadingInsightAi(false);
    insightAiGenerationTriggeredRef.current = false;
  }, []);

  const runAlreadyExistAiFlow = React.useCallback(async () => {
    if (!hasInsights || !subProjectId || isStreamingEffective) return;

    const insights = clause.insights!;
    if (insights.length > 1) {
      setShowInsightModal(true);
      return;
    }

    const crText = clientRequestPlainText.trim();
    if (!crText) return;

    const insightsForApi = formatInsightsForApi(insights, clause.module_type);
    const generation = aiSuggestionGenerationRef.current + 1;
    aiSuggestionGenerationRef.current = generation;
    setIsStreaming(true);

    let accumulatedText = '';
    let finalSuggestedText = '';

    try {
      for await (const chunk of apiService.generateInsightResponsesStream(
        subProjectId,
        crText,
        insightsForApi,
      )) {
        if (generation !== aiSuggestionGenerationRef.current) return;
        if (chunk.type === 'text') {
          accumulatedText += chunk.content || '';
        } else if (chunk.type === 'complete') {
          const suggestions = chunk.suggestions || [];
          if (suggestions.length > 0) {
            finalSuggestedText =
              suggestions[0].suggestedResponse || extractSuggestedResponse(accumulatedText);
          }
        } else if (chunk.type === 'error') {
          throw new Error(chunk.error);
        }
      }

      if (!finalSuggestedText) {
        finalSuggestedText = extractSuggestedResponse(accumulatedText);
      }
      if (finalSuggestedText) {
        applyInsightSuggestionToClientRequest(finalSuggestedText, generation);
      }
    } catch (err) {
      console.error('[CrossCheckClauseCard] AI - Already Exist failed:', err);
    } finally {
      if (generation === aiSuggestionGenerationRef.current) {
        setIsStreaming(false);
      }
    }
  }, [
    hasInsights,
    subProjectId,
    isStreamingEffective,
    clause.insights,
    clause.module_type,
    clientRequestPlainText,
    applyInsightSuggestionToClientRequest,
  ]);

  const handleAlreadyExistAI = React.useCallback(() => {
    if (!hasInsights || !subProjectId || isStreamingEffective) return;

    const insights = clause.insights ?? [];
    const insightForGoTo = pickInsightForSalesAgreementGoTo(insights);
    const canOfferSalesAgreementGoTo =
      Boolean(onGoToSalesAgreementSource) &&
      insightForGoTo != null &&
      getInsightExistingClauseSearchText(insightForGoTo).length > 0;

    if (canOfferSalesAgreementGoTo) {
      setShowViewInAgreementConfirm(true);
      return;
    }

    void runAlreadyExistAiFlow();
  }, [
    hasInsights,
    subProjectId,
    isStreamingEffective,
    clause.insights,
    onGoToSalesAgreementSource,
    runAlreadyExistAiFlow,
  ]);

  React.useEffect(() => {
    if (!showInsightModal) {
      insightAiGenerationTriggeredRef.current = false;
      return;
    }
    if (
      showInsightModal &&
      clientRequestPlainText?.trim() &&
      hasInsights &&
      !insightAiSuggestions &&
      !isLoadingInsightAi &&
      !insightAiGenerationTriggeredRef.current
    ) {
      insightAiGenerationTriggeredRef.current = true;
      void handleGenerateInsightAiSuggestions();
    }
  }, [
    showInsightModal,
    clientRequestPlainText,
    hasInsights,
    insightAiSuggestions,
    isLoadingInsightAi,
    handleGenerateInsightAiSuggestions,
  ]);

  // ─── Submit to Response (same submission state as Submit as-is) ───────────────
  const handleSubmitToResponse = React.useCallback(async () => {
    if (isSubmitting || isResponseSubmitted) return;

    const salesAgreementText = (clause.sales_agreement || '').replace(/\s+/g, ' ').trim();
    let newText: string;
    let trackChangesHtml: string;
    let searchOriginal: string;

    if (currentEditedXml) {
      // AI suggestion: the diff in currentEditedXml was computed against clientRequestPlainText
      // (the current state of the paragraph in Word), so that is the correct search target.
      newText = TrackChangesParser.extractAcceptedText(currentEditedXml).replace(/\s+/g, ' ').trim();
      trackChangesHtml = TrackChangesParser.convertOoxmlToHtml(currentEditedXml);
      searchOriginal = clientRequestPlainText.replace(/\s+/g, ' ').trim() || salesAgreementText || newText;
    } else {
      // No AI suggestion: apply the raw client request on top of the original sales agreement.
      newText = clientRequestPlainText.replace(/\s+/g, ' ').trim();
      trackChangesHtml = clientRequestXml ? TrackChangesParser.convertOoxmlToHtml(clientRequestXml) : '';
      searchOriginal = salesAgreementText || newText;
    }
    if (!searchOriginal) {
      setSubmitResult('error');
      setTimeout(() => setSubmitResult(null), 2500);
      return;
    }

    setIsSubmitting(true);
    setSubmitResult(null);
    try {
      const paragraphIndex = clause.paragraph_index;
      const useIncremental = trackChangesHtml.length > 0 && (
        trackChangesHtml.includes('track-change-insertion') || trackChangesHtml.includes('track-change-deletion')
      );
      const opsSummary = trackChangesHtml
        ? RedlineInserter.summarizeTrackChangesHtmlOps(trackChangesHtml)
        : null;
      if (currentEditedXml && trackChangesHtml) {
        const submitOoxmlAudit = TrackChangesParser.auditOoxmlTrackChangeAuthors(currentEditedXml);
        const submitHtmlAudit = TrackChangesParser.auditHtmlTrackChangeStyling(trackChangesHtml);
        console.log('[TrackChangeAuthor] submit-to-response preview styling', {
          clauseId: clause.id,
          submitOoxmlAudit,
          submitHtmlAudit,
          likelyMisstyledAiInsert:
            submitOoxmlAudit.aiSuggestionIns > 0 && submitHtmlAudit.insNewBlue === 0,
        });
      }
      const crNormForSubmit = clientRequestPlainText.replace(/\s+/g, ' ').trim();
      const appendedOnlyAtSubmit =
        crNormForSubmit.length > 0 &&
        newText.startsWith(crNormForSubmit) &&
        newText.length > crNormForSubmit.length;
      console.log('[CrossCheckClauseCard] submit-to-response: start', {
        clauseId: clause.id,
        clauseNumber: clause.clause_number,
        paragraphIndex: paragraphIndex ?? null,
        useIncremental,
        hasCurrentEditedXml: Boolean(currentEditedXml),
        searchOriginalLen: searchOriginal.length,
        newTextLen: newText.length,
        trackChangesHtmlLen: trackChangesHtml.length,
        appendedOnlyAtSubmit,
        appendedChars: appendedOnlyAtSubmit ? newText.length - crNormForSubmit.length : null,
        opsSummary,
      });

      let applied = false;
      if (currentEditedXml) {
        applied = await RedlineInserter.applyParagraphFromEditedOoxml(
          currentEditedXml,
          paragraphIndex,
        );
        console.log('[CrossCheckClauseCard] submit-to-response: OOXML paragraph apply', {
          clauseId: clause.id,
          paragraphIndex: paragraphIndex ?? null,
          applied,
        });
      }

      if (!applied && useIncremental) {
        applied = await RedlineInserter.applyWordLevelChangesIncrementally(
          searchOriginal,
          newText,
          trackChangesHtml,
          paragraphIndex,
        );
        console.log('[CrossCheckClauseCard] submit-to-response: incremental result', {
          clauseId: clause.id,
          paragraphIndex: paragraphIndex ?? null,
          incrementalApplied: applied,
          opsSummary,
        });
      }

      if (!applied) {
        throw new Error(
          'Could not apply track changes to the Word paragraph. Try selecting the clause paragraph and submit again.',
        );
      }

      // Update card status on backend only (add-in: no file/Drive ops; same endpoint as undo)
      if (subProjectId) {
        try {
          await apiService.updateClauseResponseSubmissionStatus(subProjectId, clause.id, true);
        } catch (backendErr) {
          console.warn('[CrossCheckClauseCard] Backend submit status update failed (Word changes still applied):', backendErr);
        }
      }
      onResponseSubmitted?.(clause.id);

      setSubmitResult('success');
      setTimeout(() => setSubmitResult(null), 2500);
    } catch (err) {
      console.error('[CrossCheckClauseCard] Submit to response failed:', err);
      setSubmitResult('error');
      setTimeout(() => setSubmitResult(null), 2500);
    } finally {
      setIsSubmitting(false);
    }
  }, [isSubmitting, isResponseSubmitted, clause, clientRequestPlainText, clientRequestXml, currentEditedXml, subProjectId, onResponseSubmitted]);

  /** Replace the client-request paragraph with source text — same submission/undo as Submit to Response. */
  const applySubmitAsIs = React.useCallback(
    async (sourceText: string, authorLabel: string) => {
      if (isSubmitting || isResponseSubmitted) return;

      const newText = (sourceText || '').replace(/\s+/g, ' ').trim();
      const searchOriginal = clientRequestPlainText.replace(/\s+/g, ' ').trim();
      if (!newText || !searchOriginal) {
        setSubmitResult('error');
        setTimeout(() => setSubmitResult(null), 2500);
        return;
      }

      const crXml =
        clientRequestXml || TrackChangesParser.createMinimalParagraphOoxml(clientRequestPlainText);

      setIsSubmitting(true);
      setSubmitResult(null);
      try {
        const paragraphIndex = clause.paragraph_index;
        if (paragraphIndex != null && !Number.isNaN(Number(paragraphIndex))) {
          await DocumentReader.navigateToParagraph(Number(paragraphIndex));
        } else if (searchOriginal) {
          await DocumentReader.navigateToParagraphByContent(searchOriginal);
        }

        const applied = await RedlineInserter.applySubmitAsIs(
          searchOriginal,
          newText,
          paragraphIndex,
          authorLabel,
          crXml,
        );
        if (!applied) {
          throw new Error('Could not apply track changes to the Word paragraph.');
        }

        if (subProjectId) {
          try {
            await apiService.updateClauseResponseSubmissionStatus(subProjectId, clause.id, true);
          } catch (backendErr) {
            console.warn(
              '[CrossCheckClauseCard] Submit as-is: backend status update failed (Word changes still applied):',
              backendErr,
            );
          }
        }
        onResponseSubmitted?.(clause.id);
        setSubmitResult('success');
        setTimeout(() => setSubmitResult(null), 2500);
      } catch (err) {
        console.error('[CrossCheckClauseCard] Submit as-is failed:', err);
        setSubmitResult('error');
        setTimeout(() => setSubmitResult(null), 2500);
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      isSubmitting,
      isResponseSubmitted,
      clause,
      clientRequestPlainText,
      clientRequestXml,
      subProjectId,
      onResponseSubmitted,
    ],
  );

  const handleSubmitBankOfChangesAsIs = React.useCallback(async () => {
    if (isStreaming || editingBoc) return;
    await applySubmitAsIs(displayBocText, 'Bank of Changes');
  }, [applySubmitAsIs, isStreaming, editingBoc, displayBocText]);

  const handleSubmitPastNegotiationAsIs = React.useCallback(async () => {
    if (isStreaming) return;
    await applySubmitAsIs(displayPastAgreementText, 'Past negotiations');
  }, [applySubmitAsIs, isStreaming, displayPastAgreementText]);

  // ─── Undo Response (reverts Submit to Response or Submit as-is) ─────────────
  const [isUndoing, setIsUndoing] = React.useState(false);
  const [undoNotFoundMessage, setUndoNotFoundMessage] = React.useState<string | null>(null);
  const handleUndoResponse = React.useCallback(async () => {
    if (isUndoing || !isResponseSubmitted) return;

    setIsUndoing(true);
    setUndoNotFoundMessage(null);
    try {
      const paragraphIndex = clause.paragraph_index;
      const beforeText = clause.sales_agreement || '';
      // Use in-memory timestamp from this session, or backend response_submitted_at after reopen.
      // Backend sends UTC via datetime.utcnow().isoformat() but without "Z" – parse as UTC so it matches Word's UTC dates.
      const rawBackend = clause.submission_status?.response_submitted_at;
      const asUtc = rawBackend && !/Z|[+-]\d{2}:?\d{2}$/.test(rawBackend.trim()) ? rawBackend.trim() + 'Z' : rawBackend;
      const submittedAtFromBackend = asUtc ? Date.parse(asUtc) : NaN;
      const submittedAtToUse = submittedAtForClause ?? (Number.isFinite(submittedAtFromBackend) ? submittedAtFromBackend : null);

      let reverted = false;
      if (paragraphIndex != null) {
        try {
          reverted = await RedlineInserter.revertTrackedChangesInParagraph(
            paragraphIndex,
            submittedAtToUse,
            beforeText,
          );
        } catch (wordErr) {
          console.error('[CrossCheckClauseCard] Word revert failed:', wordErr);
          throw wordErr;
        }
      }

      if (!reverted) {
        setUndoNotFoundMessage("Couldn't find the changes to revert in the document. The card has been reset to Submit.");
      }

      // Always update card status so the button goes back to "Submit to Response"
      if (subProjectId) {
        await apiService.updateClauseResponseSubmissionStatus(subProjectId, clause.id, false);
      }
      onResponseUndone?.(clause.id);
      setCurrentEditedXml(null);
    } catch (err) {
      console.error('[CrossCheckClauseCard] Undo response failed:', err);
    } finally {
      setIsUndoing(false);
    }
  }, [isUndoing, isResponseSubmitted, clause, subProjectId, submittedAtForClause, onResponseUndone]);

  // Clear undo-not-found message when clause changes or after a delay
  React.useEffect(() => {
    if (!undoNotFoundMessage) return;
    const t = setTimeout(() => setUndoNotFoundMessage(null), 8000);
    return () => clearTimeout(t);
  }, [undoNotFoundMessage]);
  React.useEffect(() => setUndoNotFoundMessage(null), [clause.id]);

  // ─── Save CR edit ────────────────────────────────────────────────────────────
  const handleSaveCrEdit = () => {
    // In a real integration this would call the backend to update the clause
    setEditingCr(false);
  };

  // ─── Save BoC edit ───────────────────────────────────────────────────────────
  const handleSaveBocEdit = () => {
    setEditingBoc(false);
  };

  const handleAddBoc = () => {
    if (!bocNewValue.trim()) { setAddingBoc(false); return; }
    // In a real integration this would call the backend to add a new BoC option
    setBocNewValue('');
    setAddingBoc(false);
  };

  const cardColor = clause.color || '#3b82f6';

  const handleGoToParagraph = React.useCallback(async () => {
    const paragraphIndex = clause.paragraph_index;
    const searchText = clientRequestPlainText?.trim();

    if (paragraphIndex != null) {
      try {
        await DocumentReader.navigateToParagraph(paragraphIndex);
        return;
      } catch (err) {
        console.warn('Navigate by index failed, falling back to content:', err);
      }
    }
    if (searchText) {
      await DocumentReader.navigateToParagraphByContent(searchText);
    }
  }, [clause.id, clause.clause_number, clause.paragraph_index, clientRequestPlainText]);

  const canGoTo = clause.paragraph_index != null || (clientRequestPlainText != null && clientRequestPlainText.trim().length > 0);

  /** Jump to existing clause: SA PDF panel when available, else Word document search. */
  const handleGoToInsightExistingClause = React.useCallback(async (insight: CrossCheckInsight) => {
    const pi = getInsightExistingClauseParagraphIndex(insight);
    if (pi != null) {
      try {
        await DocumentReader.navigateToParagraph(pi);
        return;
      } catch (err) {
        console.warn('[CrossCheckClauseCard] Go to existing clause by index failed:', err);
      }
    }
    const searchText = getInsightExistingClauseSearchText(insight);
    if (searchText) {
      await DocumentReader.navigateToParagraphByContent(searchText);
    }
  }, []);

  const handleInsightGoTo = React.useCallback(
    (insight: CrossCheckInsight) => {
      const existingClauseText = getInsightExistingClauseSearchText(insight);
      if (onGoToSalesAgreementSource && existingClauseText) {
        onGoToSalesAgreementSource(clause, existingClauseText);
        setShowInsightModal(false);
        return;
      }
      void handleGoToInsightExistingClause(insight);
    },
    [onGoToSalesAgreementSource, clause, handleGoToInsightExistingClause],
  );

  const handleViewInAgreementConfirmChoice = React.useCallback(
    (viewInAgreement: boolean) => {
      setShowViewInAgreementConfirm(false);
      if (viewInAgreement) {
        const insights = clause.insights ?? [];
        const insight = pickInsightForSalesAgreementGoTo(insights);
        if (insight) {
          handleInsightGoTo(insight);
        }
      }
      void runAlreadyExistAiFlow();
    },
    [clause.insights, handleInsightGoTo, runAlreadyExistAiFlow],
  );

  const canShowInsightGoTo = React.useCallback(
    (insight: CrossCheckInsight) =>
      Boolean(onGoToSalesAgreementSource && getInsightExistingClauseSearchText(insight)) ||
      canNavigateToInsightExistingClause(insight),
    [onGoToSalesAgreementSource],
  );

  return (
    <>
      <div
        className={`cc-clause-card${isLocked ? ' cc-locked' : ''}`}
        style={{ borderLeftColor: cardColor }}
      >
        {/* ── Header (clickable = go to paragraph in document) ─────────── */}
        <div
          ref={cardHeaderRef}
          className={`cc-clause-header${canGoTo ? ' cc-clause-header-clickable' : ''}`}
          role={canGoTo ? 'button' : undefined}
          tabIndex={canGoTo ? 0 : undefined}
          onClick={canGoTo ? handleGoToParagraph : undefined}
          onKeyDown={canGoTo ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleGoToParagraph(); } } : undefined}
          title={canGoTo ? 'Go to paragraph in document' : undefined}
        >
          <div className="cc-clause-header-left">
            <h4 className="cc-clause-title">
              Clause {clauseNumberDisplay || index + 1}
            </h4>
            <span className={`cc-clause-badge ${badge.className}`}>{badge.label}</span>
          </div>
          <div className="cc-clause-header-actions" onClick={(e) => e.stopPropagation()}>
            <button
              className={`cc-action-btn${clause.verification_status?.verified ? ' cc-verified' : ''}`}
              title={clause.verification_status?.verified ? 'Verified' : 'Not verified'}
            >
              <svg className="cc-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </button>
            <button
              type="button"
              className={`cc-action-btn${isLocked ? ' cc-locked-btn' : ''}${isUpdatingLock ? ' cc-action-btn-busy' : ''}`}
              title={isLocked ? 'Unlock clause' : 'Lock clause'}
              disabled={isUpdatingLock}
              onClick={(e) => {
                e.stopPropagation();
                onToggleLock?.(clause);
              }}
            >
              {isUpdatingLock ? (
                <span className="cc-spinner-small" aria-hidden />
              ) : isLocked ? (
                <svg className="cc-icon" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="cc-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                </svg>
              )}
            </button>

            {showKebabMenu && (
              <ClauseCardKebabMenu
                clause={clause}
                isMutating={isMutating}
                isLocked={!!isLocked}
                isClauseSubmitted={isClauseSubmitted}
                moveBlockedMessage={moveBlockedMessage}
                isMenuOpen={isMenuOpen}
                isMoveSubmenuOpen={isMoveSubmenuOpen}
                moveSubmenuPosition={moveSubmenuPosition}
                menuRef={menuRef}
                moveMenuItemRef={moveMenuItemRef}
                moveSubmenuRef={moveSubmenuRef}
                kebabVariant={kebabVariant}
                onMoveClause={onMoveClause}
                onMoveClauseMatter={onMoveClauseMatter}
                onAnalyzeAgain={onAnalyzeAgain}
                onAskAgent={onAskAgent}
                onToggleMenu={() => {
                  setIsMenuOpen((prev) => !prev);
                  setIsMoveSubmenuOpen(false);
                }}
                onToggleMoveSubmenu={() => {
                  if (!isClauseSubmitted) {
                    setIsMoveSubmenuOpen((prev) => !prev);
                  }
                }}
                onCloseMenu={() => {
                  setIsMenuOpen(false);
                  setIsMoveSubmenuOpen(false);
                }}
              />
            )}
          </div>
        </div>

        {/* ── Body: collapsed when locked (matches dashboard) ───────────────── */}
        {isLocked ? (
          <div className="cc-clause-locked-message">
            <span className="cc-clause-locked-text">
              <span className="cc-clause-locked-icon" aria-hidden>🔒</span>
              Clause is locked - click unlock to view content
            </span>
            {clause.verification_status?.verified && (
              <span className="cc-clause-verified-badge">
                <svg className="cc-verified-icon" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Verified
              </span>
            )}
          </div>
        ) : (
        <div className="cc-clause-body">
          {/* Submission chips */}
          {(clause.submission_status?.schedule_submitted || clause.submission_status?.response_submitted) && (
            <div className="cc-status-badges">
              {clause.submission_status.schedule_submitted && (
                <span className="cc-status-chip cc-schedule">Schedule Submitted</span>
              )}
              {clause.submission_status.response_submitted && (
                <span className="cc-status-chip cc-response">Response Submitted</span>
              )}
            </div>
          )}

          {/* ── CLIENT REQUEST ─────────────────────────────────────── */}
          <div className="cc-section">
            <div className="cc-section-header cc-client-request-header">
              <div className="cc-client-request-header-inner">
                <div className="cc-section-label-row">
                  <span className="cc-section-label">Client Request</span>
                  {hasInsights && (
                    <button
                      type="button"
                      className="cc-insight-circle"
                      onClick={() => setShowInsightModal(true)}
                      title={`${clause.insights!.length} insight${clause.insights!.length > 1 ? 's' : ''} available - click to view`}
                    >
                      <svg className="cc-insight-circle-icon" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M11 3a1 1 0 10-2 0v1a1 1 0 102 0V3zM15.657 5.757a1 1 0 00-1.414-1.414l-.707.707a1 1 0 001.414 1.414l.707-.707zM18 10a1 1 0 01-1 1h-1a1 1 0 110-2h1a1 1 0 011 1zM5.05 6.464A1 1 0 106.464 5.05l-.707-.707a1 1 0 00-1.414 1.414l.707.707zM5 10a1 1 0 01-1 1H3a1 1 0 110-2h1a1 1 0 011 1zM8 16v-1h4v1a2 2 0 11-4 0zM12 14c.015-.34.208-.646.477-.859a4 4 0 10-4.954 0c.27.213.462.519.476.859h4.002z" />
                      </svg>
                    </button>
                  )}
                </div>
                {(clause.track_changes_interpretation ?? primaryInsight?.explanation) && (
                  <div className="cc-explanation-row">
                    <button
                      type="button"
                      className="cc-explanation-pill"
                      onClick={() => setShowExplanationModal(true)}
                      title="Click to view track changes interpretation"
                    >
                      <svg className="cc-explanation-pill-icon" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                      Interpretation
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div
              className={`cc-client-request-content${isStreamingEffective ? ' cc-client-request-content--ai-processing' : ''}`}
              dir={clientRequestDir}
            >
              {editingCr ? (
                <div className="cc-inline-edit">
                  <textarea
                    className="cc-edit-textarea"
                    value={crEditValue}
                    onChange={e => setCrEditValue(e.target.value)}
                    dir={clientRequestDir}
                    rows={4}
                    autoFocus
                  />
                  <div className="cc-inline-edit-actions">
                    <button className="cc-btn cc-btn-save" onClick={handleSaveCrEdit}>Save</button>
                    <button className="cc-btn cc-btn-cancel" onClick={() => setEditingCr(false)}>Cancel</button>
                  </div>
                </div>
              ) : isStreamingEffective ? (
                clientRequestXml ? (
                  <ClientRequestXmlPreview xml={clientRequestXml} dir={clientRequestDir} />
                ) : (
                  <p
                    className={`cc-section-text${!clientRequestPlainText ? ' cc-empty' : ''}`}
                    dir={clientRequestDir}
                  >
                    {clientRequestPlainText || 'No client request'}
                  </p>
                )
              ) : displayXml ? (
                <ClientRequestXmlPreview xml={displayXml} dir={clientRequestDir} />
              ) : (
                <p
                  className={`cc-section-text${!clientRequestPlainText ? ' cc-empty' : ''}`}
                  dir={clientRequestDir}
                >
                  {clientRequestPlainText || 'No client request'}
                </p>
              )}
            </div>

            {/* AI + Edit (AI = bank-of-changes or past-agreement source) */}
            {!editingCr && (
              <div className="cc-action-row">
                <div className="cc-ai-btn-wrapper" ref={aiMenuRef}>
                  <button
                    className="cc-btn cc-btn-ai"
                    onClick={() => {
                      if (!hasBocForAi && !hasPaForAi) return;
                      if (hasBocForAi && hasPaForAi) {
                        setAiSourceMenuOpen((o) => !o);
                      } else {
                        handleAiSuggestionFrom(hasBocForAi ? 'bank_of_changes' : 'past_agreement');
                      }
                    }}
                    disabled={isStreamingEffective || (!hasBocForAi && !hasPaForAi) || !resolvedProjectId}
                    title={
                      hasBocForAi && hasPaForAi
                        ? 'Choose reference source for AI suggestion'
                        : hasPaForAi
                        ? 'Generate suggestion from Past negotiations'
                        : 'Generate suggestion from Bank of Changes'
                    }
                  >
                    <span className="cc-btn-star">✦</span> AI
                    {hasBocForAi && hasPaForAi && (
                      <svg className="cc-ai-chevron" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    )}
                  </button>
                  {aiSourceMenuOpen && hasBocForAi && hasPaForAi && (
                    <div className="cc-ai-source-menu">
                      <button
                        className="cc-ai-source-item"
                        onClick={() => { setAiSourceMenuOpen(false); handleAiSuggestionFrom('bank_of_changes'); }}
                      >
                        <span className="cc-ai-source-icon">📋</span>
                        <span>
                          <strong>Bank of changes</strong>
                          <span className="cc-ai-source-hint">Use selected BoC option</span>
                        </span>
                      </button>
                      <button
                        className="cc-ai-source-item"
                        onClick={() => { setAiSourceMenuOpen(false); handleAiSuggestionFrom('past_agreement'); }}
                      >
                        <span className="cc-ai-source-icon">📜</span>
                        <span>
                          <strong>Past negotiations</strong>
                          <span className="cc-ai-source-hint">Use selected past contract clause</span>
                        </span>
                      </button>
                    </div>
                  )}
                </div>
                <button
                  className="cc-btn cc-btn-edit"
                  onClick={() => {
                    setCrEditValue(clientRequestPlainText);
                    setEditingCr(true);
                  }}
                  title="Edit client request"
                >
                  <svg className="cc-btn-icon" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                  </svg>
                  Edit
                </button>
                {canAcceptTrackChanges && (
                  <button
                    type="button"
                    className="cc-btn cc-btn-accept"
                    onClick={handleAcceptClientRequestTrackChanges}
                    disabled={isStreamingEffective || isSubmitting}
                    title="Accept track changes in Word and submit this clause to response"
                  >
                    <svg className="cc-btn-icon" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {isSubmitting ? 'Accepting…' : 'Accept'}
                  </button>
                )}
                {(hasAiSuggestion || isStreamingEffective) && (
                  <button
                    type="button"
                    className="cc-btn cc-btn-clear"
                    onClick={handleClearAiSuggestion}
                    title="Clear AI suggestion and restore original client request"
                  >
                    <svg className="cc-btn-icon" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
                      <path
                        fillRule="evenodd"
                        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Clear
                  </button>
                )}
              </div>
            )}

            {/* AI - Already Exist: only when an existing clause was detected (same condition as the insight circle icon) */}
            {!editingCr && hasInsights && (
              <button
                className="cc-btn-ai-exist"
                onClick={handleAlreadyExistAI}
                disabled={isStreamingEffective || !subProjectId}
                title="Generate AI suggestion based on existing clause in Sales Agreement"
              >
                <svg className="cc-btn-bolt" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                </svg>
                AI - Already Exist
              </button>
            )}
          </div>

          {/* ── BANK OF CHANGES ─────────────────────────────────────── */}
          {bocText && (
            <div className="cc-section">
              <div className="cc-section-header">
                <div className="cc-boc-header-left">
                  <span className="cc-section-label">Bank of Changes</span>
                  {bocCount > 1 && (
                    <button
                      className="cc-boc-badge"
                      onClick={() => setBocExpanded(!bocExpanded)}
                      title={bocExpanded ? 'Collapse options' : `Show all ${bocCount} options`}
                    >
                      {bocCount}
                    </button>
                  )}
                </div>
                <button
                  className="cc-chevron-btn"
                  onClick={() => setBocExpanded(!bocExpanded)}
                >
                  <svg
                    className={`cc-chevron-icon${bocExpanded ? ' cc-open' : ''}`}
                    viewBox="0 0 20 20" fill="currentColor"
                  >
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>

              {editingBoc ? (
                <div className="cc-inline-edit">
                  <textarea
                    className="cc-edit-textarea"
                    value={bocEditValue}
                    onChange={e => setBocEditValue(e.target.value)}
                    dir={getDir(bocEditValue)}
                    rows={3}
                    autoFocus
                  />
                  <div className="cc-inline-edit-actions">
                    <button className="cc-btn cc-btn-save" onClick={handleSaveBocEdit}>Save</button>
                    <button className="cc-btn cc-btn-cancel" onClick={() => setEditingBoc(false)}>Cancel</button>
                  </div>
                </div>
              ) : (
                <>
                  {/* Primary / all options */}
                  {!bocExpanded ? (
                    <div className="cc-client-request-content" dir={getDir(displayBocText)}>
                      <p className="cc-section-text" dir={getDir(displayBocText)}>{displayBocText}</p>
                    </div>
                  ) : bocOptions ? (
                    <div className="cc-boc-options-list">
                      {bocOptions.map((opt, i) => (
                        <div
                          key={i}
                          role="button"
                          tabIndex={0}
                          className={`cc-boc-option${i === safeSelectedBocIndex ? ' cc-boc-option-primary' : ''}`}
                          onClick={() => {
                            if (isStreaming) return;
                            setAiSourceMenuOpen(false);
                            setBocExpanded(false);
                            if (i !== safeSelectedBocIndex) {
                              handleClearAiSuggestion();
                            }
                            setSelectedBocIndex(i);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              if (isStreaming) return;
                              setAiSourceMenuOpen(false);
                              setBocExpanded(false);
                              if (i !== safeSelectedBocIndex) {
                                handleClearAiSuggestion();
                              }
                              setSelectedBocIndex(i);
                            }
                          }}
                          title="Select this option"
                        >
                          <div className="cc-boc-option-rank">{opt.rank ?? i + 1}</div>
                          <p className="cc-boc-option-text" dir={getDir(opt.text)}>{opt.text}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="cc-client-request-content" dir={getDir(displayBocText)}>
                      <p className="cc-section-text" dir={getDir(displayBocText)}>{displayBocText}</p>
                    </div>
                  )}

                  {/* Add new inline form */}
                  {addingBoc && (
                    <div className="cc-inline-edit cc-inline-add">
                      <textarea
                        className="cc-edit-textarea"
                        value={bocNewValue}
                        onChange={e => setBocNewValue(e.target.value)}
                        dir={getDir(bocNewValue)}
                        placeholder="Type new option..."
                        rows={2}
                        autoFocus
                      />
                      <div className="cc-inline-edit-actions">
                        <button className="cc-btn cc-btn-save" onClick={handleAddBoc}>Add</button>
                        <button className="cc-btn cc-btn-cancel" onClick={() => { setAddingBoc(false); setBocNewValue(''); }}>Cancel</button>
                      </div>
                    </div>
                  )}

                  <div className="cc-action-row cc-action-row-boc">
                    <button
                      className="cc-btn cc-btn-add-new"
                      onClick={() => setAddingBoc(true)}
                    >
                      + Add New
                    </button>
                    <button
                      className="cc-btn cc-btn-edit"
                      onClick={() => {
                        setBocEditValue(displayBocText);
                        setEditingBoc(true);
                      }}
                    >
                      <svg className="cc-btn-icon" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                      </svg>
                      Edit
                    </button>
                    {!isResponseSubmitted && (
                      <button
                        type="button"
                        className="cc-btn cc-btn-submit-as-is"
                        onClick={() => void handleSubmitBankOfChangesAsIs()}
                        disabled={
                          isSubmitting ||
                          isUndoing ||
                          isStreaming ||
                          !displayBocText?.trim() ||
                          !clientRequestPlainText?.trim()
                        }
                        title="Same as Submit to Response: apply this Bank of Changes text to the document and mark as submitted"
                      >
                        Submit as-is
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* ── PAST AGREEMENT (Construction Loan) ──────────────────── */}
          {pastAgreementOptions && pastAgreementOptions.length > 0 && (
            <div className="cc-section">
              <div className="cc-section-header">
                <div className="cc-boc-header-left">
                  <span className="cc-section-label">Past negotiations</span>
                  {pastAgreementOptions.length > 1 && (
                    <button
                      type="button"
                      className="cc-boc-badge"
                      onClick={() => setPastAgreementExpanded(!pastAgreementExpanded)}
                      title={
                        pastAgreementExpanded
                          ? 'Collapse list'
                          : `Show all ${Math.min(pastAgreementOptions.length, 5)} past negotiations`
                      }
                    >
                      {Math.min(pastAgreementOptions.length, 5)}
                    </button>
                  )}
                </div>
                <button
                  className="cc-chevron-btn"
                  onClick={() => setPastAgreementExpanded(!pastAgreementExpanded)}
                >
                  <svg
                    className={`cc-chevron-icon${pastAgreementExpanded ? ' cc-open' : ''}`}
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </div>

              {!pastAgreementExpanded ? (
                <div className="cc-pa-collapsed-block">
                  {selectedPastAgreementOption && (
                    <>
                      {(selectedPastAgreementOption.explanation?.trim() ||
                        selectedPastAgreementOption.id ||
                        selectedPastAgreementOption.similarity != null) && (
                        <div className="cc-pa-badge-row">
                          {selectedPastAgreementOption.explanation?.trim() && (
                            <button
                              type="button"
                              className="cc-pa-explanation-badge"
                              onClick={() =>
                                setPastAgreementExplanationModal({
                                  text: selectedPastAgreementOption.explanation!.trim(),
                                  rank: safeSelectedPastAgreementIndex + 1,
                                })
                              }
                            >
                              Explanation
                            </button>
                          )}
                          {selectedPastAgreementOption.id && (
                            <button
                              type="button"
                              className="cc-pa-source-badge"
                              onClick={() =>
                                setPastAgreementSourceModal({
                                  rank: safeSelectedPastAgreementIndex + 1,
                                  playbookId: selectedPastAgreementOption.id,
                                  searchText: getPastAgreementPlainText(
                                    selectedPastAgreementOption.id,
                                    selectedPastAgreementOption.text,
                                  ),
                                })
                              }
                            >
                              <svg
                                className="cc-pa-source-badge-icon"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                                aria-hidden
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z"
                                  clipRule="evenodd"
                                />
                              </svg>
                              Source
                            </button>
                          )}
                          {selectedPastAgreementOption.similarity != null &&
                            !Number.isNaN(selectedPastAgreementOption.similarity) && (
                            <span className="cc-pa-similarity-badge">
                              Similarity: {selectedPastAgreementOption.similarity}%
                            </span>
                          )}
                        </div>
                      )}
                      <div className="cc-client-request-content" dir={selectedPastAgreementDir}>
                        {selectedPastAgreementHtml ? (
                          <div
                            className={`cc-paragraph-xml-display${
                              selectedPastAgreementSide ? ` cc-side-${selectedPastAgreementSide}` : ''
                            }`}
                            dir={selectedPastAgreementDir}
                          >
                            <div
                              className="cc-selected-text-xml"
                              dangerouslySetInnerHTML={{ __html: selectedPastAgreementHtml }}
                              dir={selectedPastAgreementDir}
                            />
                          </div>
                        ) : (() => {
                            const collapsedPlainText = selectedPastAgreementOption.id
                              ? getPastAgreementPlainText(selectedPastAgreementOption.id, selectedPastAgreementOption.text)
                              : (selectedPastAgreementOption.text ?? '');
                            return collapsedPlainText ? (
                              <p className="cc-section-text" dir={getDir(collapsedPlainText)}>{collapsedPlainText}</p>
                            ) : (
                              <p className="cc-section-text cc-empty">
                                XML not available for this playbook record.
                              </p>
                            );
                          })()}
                        {isPastAgreementXmlLoading && (
                          <p className="cc-section-text cc-empty">Loading playbook XML…</p>
                        )}
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <div className="cc-boc-options-list">
                  {pastAgreementOptions.slice(0, 5).map((opt, i) => (
                    (() => {
                      const optionXml = opt.id ? (pastAgreementXmlById[opt.id] || null) : null;
                      const optionSide = opt.id ? (pastAgreementSideById[opt.id] || null) : null;
                      const optionPlainText = opt.id ? getPastAgreementPlainText(opt.id, opt.text) : (opt.text ?? '');
                      let optionHtml: string | null = null;
                      let optionDir: 'rtl' | 'ltr' = getDir(optionPlainText) as 'rtl' | 'ltr';
                      if (optionXml) {
                        try {
                          optionHtml = TrackChangesParser.convertOoxmlToHtml(optionXml);
                          optionDir = getDir(TrackChangesParser.extractText(optionXml)) as 'rtl' | 'ltr';
                        } catch {
                          optionHtml = null;
                        }
                      }
                      return (
                    <div
                      key={opt.id || i}
                      role="button"
                      tabIndex={0}
                      className={`cc-boc-option cc-pa-option-readonly${
                        i === safeSelectedPastAgreementIndex ? ' cc-boc-option-primary' : ''
                      }${isStreaming ? ' cc-pa-option-disabled' : ''}`}
                      onClick={() => {
                        if (isStreaming) return;
                        setAiSourceMenuOpen(false);
                        setPastAgreementExpanded(false);
                        if (i !== safeSelectedPastAgreementIndex) {
                          handleClearAiSuggestion();
                        }
                        setSelectedPastAgreementIndex(i);
                      }}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          if (isStreaming) return;
                          setAiSourceMenuOpen(false);
                          setPastAgreementExpanded(false);
                          if (i !== safeSelectedPastAgreementIndex) {
                            handleClearAiSuggestion();
                          }
                          setSelectedPastAgreementIndex(i);
                        }
                      }}
                      title="Select this past negotiation"
                    >
                      <div className="cc-boc-option-rank">{i + 1}</div>
                      {(opt.explanation?.trim() || opt.id || opt.similarity != null) && (
                        <div className="cc-pa-option-main">
                          <div className="cc-pa-badge-row">
                            {opt.explanation?.trim() && (
                              <button
                                type="button"
                                className="cc-pa-explanation-badge"
                                onClick={e => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  setPastAgreementExplanationModal({
                                    text: opt.explanation!.trim(),
                                    rank: i + 1,
                                  });
                                }}
                              >
                                Explanation
                              </button>
                            )}
                            {opt.id && (
                              <button
                                type="button"
                                className="cc-pa-source-badge"
                                onClick={e => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  setPastAgreementSourceModal({
                                    rank: i + 1,
                                    playbookId: opt.id,
                                    searchText: getPastAgreementPlainText(opt.id, opt.text),
                                  });
                                }}
                              >
                                <svg
                                  className="cc-pa-source-badge-icon"
                                  viewBox="0 0 20 20"
                                  fill="currentColor"
                                  aria-hidden
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                                Source
                              </button>
                            )}
                            {opt.similarity != null && !Number.isNaN(opt.similarity) && (
                              <span className="cc-pa-similarity-badge">
                                Similarity: {opt.similarity}%
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                      {optionHtml ? (
                        <div
                          className={`cc-paragraph-xml-display${
                            optionSide ? ` cc-side-${optionSide}` : ''
                          }`}
                          dir={optionDir}
                        >
                          <div
                            className="cc-selected-text-xml"
                            dangerouslySetInnerHTML={{ __html: optionHtml }}
                            dir={optionDir}
                          />
                        </div>
                      ) : optionPlainText ? (
                        <p className="cc-boc-option-text" dir={optionDir}>{optionPlainText}</p>
                      ) : (
                        <p className="cc-boc-option-text cc-empty">
                          XML not available for this playbook record.
                        </p>
                      )}
                    </div>
                      );
                    })()
                  ))}
                </div>
              )}

              {!isResponseSubmitted && (
                <div className="cc-action-row">
                  <button
                    type="button"
                    className="cc-btn cc-btn-submit-as-is"
                    onClick={() => void handleSubmitPastNegotiationAsIs()}
                    disabled={
                      isSubmitting ||
                      isUndoing ||
                      isStreaming ||
                      !displayPastAgreementText?.trim() ||
                      !clientRequestPlainText?.trim()
                    }
                    title="Same as Submit to Response: apply this past negotiation text to the document and mark as submitted"
                  >
                    Submit as-is
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ── SALES AGREEMENT ─────────────────────────────────────── */}
          {clause.sales_agreement && (
            <div className="cc-section">
              {/* Header: title (expand) on the left; trailing group = "Go to" then chevron (expand). */}
              <div className="cc-sa-section-header">
                <button
                  type="button"
                  className="cc-sa-title-toggle"
                  onClick={() => setSalesExpanded(!salesExpanded)}
                  aria-expanded={salesExpanded}
                >
                  <span className="cc-section-label cc-sa-label">Sales Agreement</span>
                </button>
                <div className="cc-sa-header-trailing">
                  {onGoToSalesAgreementSource && (
                    <SalesAgreementGoToBadge
                      onClick={() => onGoToSalesAgreementSource(clause)}
                      disabled={isSalesAgreementSourceLoading}
                      isActive={isSalesAgreementSourceActive}
                      isLoading={isSalesAgreementSourceLoading}
                    />
                  )}
                  <button
                    type="button"
                    className="cc-chevron-btn cc-sa-chevron-btn"
                    onClick={() => setSalesExpanded(!salesExpanded)}
                    aria-expanded={salesExpanded}
                    aria-label={salesExpanded ? 'Collapse Sales Agreement' : 'Expand Sales Agreement'}
                  >
                    <svg
                      className={`cc-chevron-icon${salesExpanded ? ' cc-open' : ''}`}
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                </div>
              </div>
              {salesExpanded ? (
                <div className="cc-collapsible-content" dir={salesAgreementDir}>
                  <p className="cc-section-text cc-sa-content" dir={salesAgreementDir}>
                    {clause.sales_agreement.replace(/\s+/g, ' ').trim()}
                  </p>
                </div>
              ) : (
                <p className="cc-sa-preview" dir={salesAgreementDir}>
                  {clause.sales_agreement.replace(/\s+/g, ' ').trim().slice(0, 60)}...
                </p>
              )}
            </div>
          )}

          {/* ── Horizontal line then Submit / Undo Response ──────────────────── */}
          <div className="cc-submit-row">
            {isResponseSubmitted ? (
              <button
                className={`cc-submit-btn cc-undo-response${isUndoing ? ' cc-undoing' : ''}`}
                disabled={isUndoing || isSubmitting}
                onClick={handleUndoResponse}
              >
                {isUndoing ? (
                  <>
                    <div className="cc-submit-spinner" />
                    Undoing…
                  </>
                ) : (
                  <>
                    <svg className="cc-submit-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M3 10h10a5 5 0 015 5v2M3 10l4-4m-4 4l4 4" />
                    </svg>
                    Undo Response
                  </>
                )}
              </button>
            ) : (
              <button
                className={`cc-submit-btn${submitResult === 'success' ? ' cc-submit-ok' : ''}${submitResult === 'error' ? ' cc-submit-err' : ''}`}
                disabled={isSubmitting || isUndoing}
                onClick={handleSubmitToResponse}
              >
                {isSubmitting ? (
                  <>
                    <div className="cc-submit-spinner" />
                    Applying…
                  </>
                ) : submitResult === 'success' ? (
                  <>✓ Applied to Document</>
                ) : submitResult === 'error' ? (
                  <>⚠ Could not apply</>
                ) : (
                  <>
                    <svg className="cc-submit-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Submit to Response
                  </>
                )}
              </button>
            )}
          </div>
          {undoNotFoundMessage && (
            <div className="cc-undo-not-found-msg" role="alert">
              {undoNotFoundMessage}
            </div>
          )}
        </div>
        )}
      </div>

      {/* ── PAST AGREEMENT: per-option explanation (Construction Loan) ───────── */}
      {pastAgreementExplanationModal && (
        <div
          className="cc-modal-overlay"
          onClick={() => setPastAgreementExplanationModal(null)}
        >
          <div className="cc-modal" onClick={e => e.stopPropagation()}>
            <div className="cc-modal-header">
              <span className="cc-modal-title">
                Past negotiations — Explanation (option {pastAgreementExplanationModal.rank})
              </span>
              <button
                type="button"
                className="cc-modal-close"
                onClick={() => setPastAgreementExplanationModal(null)}
              >
                ✕
              </button>
            </div>
            <div className="cc-modal-body">
              <p
                className="cc-modal-explanation-text"
                dir={getDir(pastAgreementExplanationModal.text)}
              >
                {pastAgreementExplanationModal.text}
              </p>
            </div>
          </div>
        </div>
      )}

      {pastAgreementSourceModal && resolvedProjectId && (
        <PastNegotiationSourceModal
          filename={getPastAgreementSourceDisplay(pastAgreementSourceModal.playbookId)}
          filenameDir={getDir(getPastAgreementSourceDisplay(pastAgreementSourceModal.playbookId))}
          projectId={resolvedProjectId}
          playbookId={pastAgreementSourceModal.playbookId}
          searchText={pastAgreementSourceModal.searchText}
          domain={pastAgreementDomain}
          onClose={() => setPastAgreementSourceModal(null)}
        />
      )}

      {/* ── EXPLANATION MODAL (Track Changes Interpretation; matches dashboard) ── */}
      {showExplanationModal && (clause.track_changes_interpretation ?? primaryInsight?.explanation) && (
        <div className="cc-modal-overlay" onClick={() => setShowExplanationModal(false)}>
          <div className="cc-modal" onClick={e => e.stopPropagation()}>
            <div className="cc-modal-header">
              <span className="cc-modal-title">Track Changes Interpretation</span>
              <button className="cc-modal-close" onClick={() => setShowExplanationModal(false)}>✕</button>
            </div>
            <div className="cc-modal-body">
              <p className="cc-modal-explanation-text" dir={getDir(clause.track_changes_interpretation ?? primaryInsight?.explanation ?? '')}>
                {clause.track_changes_interpretation ?? primaryInsight?.explanation ?? ''}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ── View existing clause in Sales Agreement? (AI - Already Exist) ── */}
      {showViewInAgreementConfirm && (
        <div
          className="cc-modal-overlay cc-modal-overlay--confirm"
          onClick={() => handleViewInAgreementConfirmChoice(false)}
        >
          <div
            className="cc-modal cc-modal--confirm"
            role="dialog"
            aria-labelledby="cc-view-in-agreement-title"
            aria-describedby="cc-view-in-agreement-desc"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="cc-modal-header">
              <span className="cc-modal-title" id="cc-view-in-agreement-title">
                View in Sales Agreement?
              </span>
            </div>
            <div className="cc-modal-body cc-modal-body--confirm">
              <p className="cc-confirm-message" id="cc-view-in-agreement-desc">
                An existing clause was detected in the Sales Agreement. Would you like to open it
                in the agreement viewer?
              </p>
              <div className="cc-confirm-actions">
                <button
                  type="button"
                  className="cc-btn cc-btn-cancel"
                  onClick={() => handleViewInAgreementConfirmChoice(false)}
                >
                  Continue without viewing
                </button>
                <button
                  type="button"
                  className="cc-btn cc-btn-save"
                  onClick={() => handleViewInAgreementConfirmChoice(true)}
                >
                  View in agreement
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── INSIGHTS MODAL (dashboard "Already Existing Clauses" design) ── */}
      {showInsightModal && (
        <div className="cc-modal-overlay" onClick={closeInsightModal}>
          <div className="cc-modal cc-modal--insights" onClick={e => e.stopPropagation()}>
            <div className="cc-modal-header cc-modal-header--insights">
              <div className="cc-modal-header-title-row">
                <InsightsLightbulbIcon className="cc-insights-header-icon" />
                <span className="cc-modal-title">Already Existing Clauses</span>
              </div>
              <button type="button" className="cc-modal-close cc-modal-close--insights" onClick={closeInsightModal}>
                ×
              </button>
            </div>
            <div className="cc-modal-body cc-modal-body--insights">
              <p className="cc-insights-intro">
                The following clauses already exist in the Sales Agreement and may already fulfill this client request.
                <span className="cc-insights-intro-hint">💡 Click on any clause to scroll to it in the document.</span>
              </p>
              <div className="cc-insights-list">
              {(clause.insights ?? []).map((insight, i) => {
                const aiSuggestion = insightAiSuggestions?.[i];
                const isGeneratingThis = isLoadingInsightAi && !aiSuggestion?.suggestedResponse;
                return (
                <div key={i} className="cc-insight-card">
                  <div className="cc-insight-card-main">
                    <div className="cc-insight-card-row">
                      <span className="cc-insight-number">{i + 1}</span>
                      <div className="cc-insight-card-content">
                        <div className="cc-insight-label-row">
                          <h4 className="cc-insight-label">Existing Clause:</h4>
                          {insight.similarity_score !== undefined && (
                            <span className="cc-insight-similarity-badge">
                              {(insight.similarity_score * 100).toFixed(0)}% Similar
                            </span>
                          )}
                          <div className="cc-modal-insight-section-actions">
                          {canShowInsightGoTo(insight) && (
                            <SalesAgreementGoToBadge
                              className="cc-insight-go-to-badge"
                              onClick={() => handleInsightGoTo(insight)}
                              isActive={isSalesAgreementSourceActive}
                              isLoading={isSalesAgreementSourceLoading}
                            />
                          )}
                          </div>
                        </div>
                        {insight.exist_clause && (
                          <div className="cc-insight-clause-box" dir={getDir(insight.exist_clause)}>
                            {insight.exist_clause}
                          </div>
                        )}
                        {insight.explanation && (
                          <div className="cc-insight-explanation">
                            <h4 className="cc-insight-label">Explanation:</h4>
                            <p dir={getDir(insight.explanation)}>{insight.explanation}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {(insight.suggested_xml || isLoadingInsightAi || aiSuggestion) && (
                    <div className="cc-insight-ai-section" onClick={e => e.stopPropagation()}>
                      <div className="cc-insight-ai-header">
                        {(isGeneratingThis || isLoadingInsightAi) && (
                          <span className="cc-insight-ai-spinner" aria-hidden />
                        )}
                        {!isGeneratingThis && !isLoadingInsightAi && (
                          <InsightsLightbulbIcon className="cc-insight-ai-icon" />
                        )}
                        <h4 className="cc-insight-ai-title">
                          AI Suggested Response{(isGeneratingThis || (isLoadingInsightAi && !aiSuggestion?.suggestedResponse)) ? ' (generating...)' : ''}:
                        </h4>
                      </div>
                      <div className="cc-insight-ai-box">
                        {insight.suggested_xml ? (
                          <ClientRequestXmlPreview xml={insight.suggested_xml} dir={getDir(insight.suggested_xml)} />
                        ) : isGeneratingThis ? (
                          <div className="cc-insight-ai-generating">
                            <span className="cc-insight-ai-spinner" aria-hidden />
                            Generating AI suggestion...
                          </div>
                        ) : aiSuggestion?.suggestedResponse ? (
                          <>
                            <InsightSuggestionPreview
                              suggestedText={aiSuggestion.suggestedResponse}
                              clientRequestXml={clientRequestXml}
                              clientRequestPlainText={clientRequestPlainText}
                            />
                            <div className="cc-insight-ai-actions">
                              <button
                                type="button"
                                className="cc-insight-apply-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleApplyInsightSuggestion(aiSuggestion.suggestedResponse);
                                }}
                              >
                                Apply This Response
                              </button>
                            </div>
                          </>
                        ) : (
                          <p className="cc-modal-empty">No suggestion generated.</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                );
              })}
              {(!clause.insights || clause.insights.length === 0) && (
                <p className="cc-modal-empty">No insights available.</p>
              )}
              </div>
            </div>
            <div className="cc-modal-footer cc-modal-footer--insights">
              <button
                type="button"
                className="cc-insights-generate-btn"
                onClick={() => void handleGenerateInsightAiSuggestions()}
                disabled={isLoadingInsightAi}
              >
                {isLoadingInsightAi ? (
                  <>
                    <span className="cc-insight-ai-spinner cc-insight-ai-spinner--white" aria-hidden />
                    Generating...
                  </>
                ) : (
                  <>
                    <InsightsLightbulbIcon className="cc-insights-generate-icon" />
                    AI Suggestions
                  </>
                )}
              </button>
              <button type="button" className="cc-insights-close-btn" onClick={closeInsightModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

    </>
  );
};

function normalizeClauseMatter(matter?: string): CrossCheckMatter {
  const m = (matter || 'other').trim().toLowerCase();
  if (m === 'commercial' || m === 'legal' || m === 'other') {
    return m;
  }
  return 'other';
}

interface ClauseCardKebabMenuProps {
  clause: CrossCheckClause;
  kebabVariant: 'mcc' | 'construction-loan';
  isMutating: boolean;
  isLocked: boolean;
  isClauseSubmitted: boolean;
  moveBlockedMessage: string;
  isMenuOpen: boolean;
  isMoveSubmenuOpen: boolean;
  moveSubmenuPosition: { top: number; left: number } | null;
  menuRef: React.Ref<HTMLDivElement>;
  moveMenuItemRef: React.Ref<HTMLButtonElement>;
  moveSubmenuRef: React.Ref<HTMLDivElement>;
  onMoveClause?: (
    clause: CrossCheckClause,
    target: 'sales_agreement' | 'appendix_a' | 'saved_for_later',
  ) => void;
  onMoveClauseMatter?: (clause: CrossCheckClause, target: CrossCheckMatter) => void;
  onAnalyzeAgain?: (clause: CrossCheckClause) => void;
  onAskAgent?: (clause: CrossCheckClause) => void;
  onToggleMenu: () => void;
  onToggleMoveSubmenu: () => void;
  onCloseMenu: () => void;
}

const ClauseCardKebabMenu: React.FC<ClauseCardKebabMenuProps> = ({
  clause,
  kebabVariant,
  isMutating,
  isLocked,
  isClauseSubmitted,
  moveBlockedMessage,
  isMenuOpen,
  isMoveSubmenuOpen,
  moveSubmenuPosition,
  menuRef,
  moveMenuItemRef,
  moveSubmenuRef,
  onMoveClause,
  onMoveClauseMatter,
  onAnalyzeAgain,
  onAskAgent,
  onToggleMenu,
  onToggleMoveSubmenu,
  onCloseMenu,
}) => {
  const isClKebab = kebabVariant === 'construction-loan';
  const currentMatter = normalizeClauseMatter(clause.matter);
  const showMove = isClKebab ? Boolean(onMoveClauseMatter) : Boolean(onMoveClause);
  const showAnalyzeAgain =
    Boolean(onAnalyzeAgain) &&
    (isClKebab ||
      clause.documentType === 'sales_agreement' ||
      clause.documentType === 'appendix_a');

  return (
  <div className="cc-kebab-wrap" ref={menuRef}>
    <button
      type="button"
      className={`cc-action-btn cc-kebab-btn${isMutating || isLocked ? ' cc-kebab-disabled' : ''}`}
      onClick={(e) => {
        e.stopPropagation();
        onToggleMenu();
      }}
      disabled={isMutating || isLocked}
      title={isLocked ? 'Unlock to access actions' : 'More actions'}
      aria-haspopup="menu"
      aria-expanded={isMenuOpen}
    >
      {isMutating ? (
        <span className="cc-spinner-small" aria-hidden />
      ) : (
        <MoreVertical className="cc-icon" aria-hidden />
      )}
    </button>

    {isMenuOpen && (
      <div className="cc-kebab-menu" role="menu" onClick={(e) => e.stopPropagation()}>
        {showMove && (
          <div className="cc-kebab-move-wrap">
            <button
              ref={moveMenuItemRef}
              type="button"
              role="menuitem"
              disabled={isClauseSubmitted}
              title={isClauseSubmitted ? moveBlockedMessage : undefined}
              className={`cc-kebab-item${isClauseSubmitted ? ' cc-kebab-item-disabled' : ''}`}
              onClick={onToggleMoveSubmenu}
            >
              <span>Move</span>
              <svg className="cc-kebab-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
            {isMoveSubmenuOpen && !isClauseSubmitted && moveSubmenuPosition && createPortal(
              <div
                ref={moveSubmenuRef}
                className="cc-kebab-submenu"
                style={{
                  top: moveSubmenuPosition.top,
                  left: moveSubmenuPosition.left,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {(isClKebab
                  ? ([
                      { key: 'commercial' as const, label: 'Commercial' },
                      { key: 'legal' as const, label: 'Legal' },
                      { key: 'other' as const, label: 'Other' },
                    ] as const)
                  : ([
                      { key: 'sales_agreement' as const, label: 'Sales Agreement' },
                      { key: 'appendix_a' as const, label: 'Appendix A' },
                      { key: 'saved_for_later' as const, label: 'Saved' },
                    ] as const)
                ).map((target) => {
                  const isCurrent = isClKebab
                    ? currentMatter === target.key
                    : clause.documentType === target.key;
                  return (
                    <button
                      key={target.key}
                      type="button"
                      disabled={isCurrent}
                      className={`cc-kebab-item${isCurrent ? ' cc-kebab-item-disabled' : ''}`}
                      onClick={() => {
                        onCloseMenu();
                        if (!isCurrent) {
                          if (isClKebab && onMoveClauseMatter) {
                            onMoveClauseMatter(clause, target.key as CrossCheckMatter);
                          } else if (!isClKebab && onMoveClause) {
                            onMoveClause(
                              clause,
                              target.key as 'sales_agreement' | 'appendix_a' | 'saved_for_later',
                            );
                          }
                        }
                      }}
                    >
                      <span>{target.label}</span>
                      {isCurrent && <span className="cc-kebab-current">current</span>}
                    </button>
                  );
                })}
              </div>,
              document.body,
            )}
          </div>
        )}
        {showAnalyzeAgain && (
          <button
            type="button"
            role="menuitem"
            className="cc-kebab-item"
            onClick={() => {
              onCloseMenu();
              onAnalyzeAgain?.(clause);
            }}
          >
            Analyze again
          </button>
        )}
        {onAskAgent && (
          <button
            type="button"
            role="menuitem"
            className="cc-kebab-item"
            onClick={() => {
              onCloseMenu();
              onAskAgent(clause);
            }}
          >
            Ask Agent
          </button>
        )}
      </div>
    )}
  </div>
  );
};

export default CrossCheckClauseCard;
